// Informational warning panel surfaced above the submit button on every
// New / Edit form (and in the admin SubmissionDetail panel). Lists up
// to five existing entries that look similar to what the user is
// drafting, so they can either correct the draft, link to the existing
// one, or proceed if they're confident their entry is distinct.
//
// We deliberately don't block submission — domain experts know
// distinctions (different editions, different events with the same
// title) that a trigram match can't pick up.

import { useTranslation } from 'react-i18next';

// Render the small metadata line under each candidate row. Each kind
// surfaces the fields that best distinguish "is this the same thing":
// authors+year for bibliography, university for scholars, etc.
function CandidateMeta({ candidate, kind }) {
  switch (kind) {
    case 'bibliography':
      return (
        <>
          {(candidate.authors ?? []).join(', ') || '—'}
          {candidate.year ? ` (${candidate.year})` : ''}
          {candidate.type ? ` · ${candidate.type}` : ''}
        </>
      );
    case 'scholars':
      return (
        <>
          {candidate.university || '—'}
          {candidate.email_public ? ` · ${candidate.email_public}` : ''}
        </>
      );
    case 'organizations':
      return (
        <>
          {candidate.type || '—'}
          {candidate.website ? ` · ${candidate.website}` : ''}
        </>
      );
    case 'teaching':
      return (
        <>
          {candidate.type || '—'}
          {candidate.url ? ` · ${candidate.url}` : ''}
        </>
      );
    case 'research_tools':
      return (
        <>
          {candidate.type || '—'}
          {candidate.url ? ` · ${candidate.url}` : ''}
        </>
      );
    case 'broader_engagements':
      return (
        <>
          {candidate.category || '—'}
          {candidate.author ? ` · ${candidate.author}` : ''}
        </>
      );
    case 'whats_new':
      return (
        <>
          {candidate.type || '—'}
          {candidate.date_start ? ` · ${candidate.date_start}` : ''}
        </>
      );
    default:
      return null;
  }
}

export default function SimilarEntriesWarning({ candidates, kind }) {
  const { t } = useTranslation();
  if (!candidates || candidates.length === 0) return null;

  return (
    <div className="my-4 rounded-md border border-gold/40 bg-navy-mid/30 p-4">
      <h3 className="font-serif text-base font-semibold text-gold-light">
        {t('similar.title', { count: candidates.length })}
      </h3>
      <p className="mt-1 text-xs text-ink-secondary">{t('similar.body')}</p>
      <ul className="mt-3 flex flex-col gap-2">
        {candidates.map((c) => {
          const pct = Math.round((c.similarity_score ?? 0) * 100);
          return (
            <li
              key={c.id}
              className="rounded border border-white/10 bg-navy-deep/40 p-3 text-sm"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-cream">{c.title || c.name}</span>
                <span className="text-[0.7rem] text-gold/70">
                  {t('similar.match', { pct })}
                </span>
              </div>
              <div className="mt-1 text-xs text-ink-muted">
                <CandidateMeta candidate={c} kind={kind} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
